import { useEffect, useRef } from 'react';
import { datosDelPlano, type PlanoInventario } from '@supernova/shared';
import { IfcViewer3D } from '../IfcViewer3D.js';
import { subirMiniaturaPlano } from '../../api/planos.js';

/** Cuánto se espera a que el modelo se dibuje antes de darlo por fallido (3 min). */
const ESPERA_MAXIMA_MS = 3 * 60 * 1000;

/**
 * Vista previa de un IFC SIN que nadie lo abra: monta el visor 3D fuera de la pantalla, toma la
 * misma foto que toma al abrirlo (`onPrimeraVista`), la sube junto con la cuenta de elementos y
 * avisa para desmontarse. Todo en el navegador, sin IA, $0. Quien lo usa decide qué modelos pasan
 * (uno a la vez y solo los que se abren sin pedir confirmación por tamaño).
 */
export function MiniaturaIfcEnSegundoPlano({
  projectId,
  fileId,
  onTermino,
}: {
  projectId: string;
  fileId: string;
  /** Se llama una sola vez, haya salido o no: el que lo montó lo desmonta y sigue con otro. */
  onTermino: (ok: boolean) => void;
}) {
  const inventarioRef = useRef<PlanoInventario | null>(null);
  const terminoRef = useRef(false);
  const terminar = (ok: boolean) => {
    if (terminoRef.current) return;
    terminoRef.current = true;
    onTermino(ok);
  };

  // Si el modelo no llega a dibujarse (archivo dañado, falla del visor), no se espera para siempre:
  // la cola sigue con el próximo y esta ficha queda con el ícono. Constante técnica, no de negocio.
  useEffect(() => {
    const t = setTimeout(() => terminar(false), ESPERA_MAXIMA_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      aria-hidden
      style={{ position: 'fixed', left: -10000, top: 0, width: 640, height: 400, pointerEvents: 'none', opacity: 0 }}
    >
      <IfcViewer3D
        projectId={projectId}
        fileId={fileId}
        sinBarraDeEstado
        onInventario={(inv) => { inventarioRef.current = inv; }}
        onPrimeraVista={(imagen) => {
          subirMiniaturaPlano(projectId, fileId, imagen, datosDelPlano('ifc', inventarioRef.current))
            .then(() => terminar(true))
            .catch(() => terminar(false));
        }}
      />
    </div>
  );
}
